import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Search, Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { externalSupabase } from "@/lib/externalSupabase";
import ListingCard from "@/components/ListingCard";
import DirectResultCard from "@/components/DirectResultCard";
import EmailCaptureModal from "@/components/EmailCaptureModal";
import BookingDetailsForm, { type BookingDetails } from "@/components/BookingDetailsForm";

type LensMatch = {
  title: string;
  link: string;
  source: string;
  thumbnail: string;
  snippet?: string;
  isOwnerSite?: boolean;
  reasoning?: string;
};

export type SearchResult = {
  propertyTitle: string;
  hostName: string;
  platformPrice: number | null;
  directPrice: number | null;
  directUrl: string | null;
  platform: "airbnb" | "vrbo";
  savingsPercent: number | null;
  confidence?: string;
  lensMatches: LensMatch[];
  foundDirect: boolean;
  foundOwnerSite?: boolean;
  realPropertyName?: string | null;
  knowledgeGraphUrl?: string | null;
  lensRelatedSearches?: string[];
  mainImageUrl?: string | null;
};

const SearchPage = () => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState<string | null>(null);
  const [manualBooking, setManualBooking] = useState<BookingDetails>({
    checkIn: null,
    checkOut: null,
    adults: 1,
    children: 0,
  });

  // Auto-fill email from logged-in user (check both auth clients)
  useEffect(() => {
    const checkSessions = async () => {
      // Check internal supabase auth first
      const { data: { session: internalSession } } = await supabase.auth.getSession();
      if (internalSession?.user?.email) {
        setCapturedEmail(internalSession.user.email);
        return;
      }
      // Fallback: check external supabase auth (magic link from dashboard)
      const { data: { session: externalSession } } = await externalSupabase.auth.getSession();
      if (externalSession?.user?.email) {
        setCapturedEmail(externalSession.user.email);
      }
    };
    checkSessions();

    const { data: { subscription: internalSub } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.email) {
        setCapturedEmail(session.user.email);
      }
    });
    const { data: { subscription: externalSub } } = externalSupabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.email) {
        setCapturedEmail(session.user.email);
      }
    });
    return () => {
      internalSub.unsubscribe();
      externalSub.unsubscribe();
    };
  }, []);

  // Detect if the URL already has dates/guests
  const urlMissingDetails = useMemo(() => {
    try {
      const parsed = new URL(url);
      const hasCheckIn = !!parsed.searchParams.get("check_in");
      const hasCheckOut = !!parsed.searchParams.get("check_out");
      const hasAdults = !!parsed.searchParams.get("adults");
      return !hasCheckIn || !hasCheckOut || !hasAdults;
    } catch {
      return false;
    }
  }, [url]);

  const [verifiedProperty, setVerifiedProperty] = useState<{
    owner_website: string;
    original_name: string | null;
    masked_title: string | null;
    image_url: string | null;
    city: string | null;
    country: string | null;
  } | null>(null);

  const checkVerifiedProperty = async (listingUrl: string) => {
    try {
      const { data, error } = await externalSupabase
        .from("properties")
        .select("is_verified, owner_website, original_name, masked_title, image_url, city, country")
        .eq("airbnb_url", listingUrl)
        .eq("is_verified", true)
        .maybeSingle();

      if (!error && data && data.owner_website) {
        return data;
      }
    } catch (err) {
      console.error("Verified check failed:", err);
    }
    return null;
  };

  const handleSearchClick = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;

    const lower = url.toLowerCase();
    if (!lower.includes("airbnb") && !lower.includes("vrbo")) {
      setError("Please enter a valid Airbnb or Vrbo listing URL.");
      return;
    }

    setError(null);
    setVerifiedProperty(null);

    // Check for verified property
    const verified = await checkVerifiedProperty(url);
    if (verified) {
      setVerifiedProperty(verified as any);
    }

    // Always show email modal first if no email yet
    if (!capturedEmail) {
      setShowEmailModal(true);
      return;
    }

    // Already have email
    if (verified) {
      // Instant match — just show the result, no AI search needed
      buildInstantResult(verified as any);
    } else {
      runSearch();
    }
  };

  const buildInstantResult = (verified: NonNullable<typeof verifiedProperty>) => {
    const instantResult: SearchResult = {
      propertyTitle: verified.masked_title || "Verified Property",
      hostName: "Verified Host",
      platformPrice: null,
      directPrice: null,
      directUrl: verified.owner_website,
      platform: "airbnb",
      savingsPercent: null,
      confidence: "verified",
      lensMatches: [{
        title: verified.original_name || verified.masked_title || "Owner's Direct Site",
        link: verified.owner_website!,
        source: "Verified",
        thumbnail: verified.image_url || "",
        isOwnerSite: true,
        reasoning: "This property has been verified by bnbxo.",
      }],
      foundDirect: true,
      foundOwnerSite: true,
      realPropertyName: verified.original_name || verified.masked_title,
      knowledgeGraphUrl: null,
      lensRelatedSearches: [],
      mainImageUrl: verified.image_url,
    };
    setResult(instantResult);
  };

  const handleEmailSuccess = (email: string) => {
    setCapturedEmail(email);
    setShowEmailModal(false);

    if (verifiedProperty) {
      buildInstantResult(verifiedProperty);
    } else {
      runSearch();
    }
  };

  const runSearch = async () => {
    setLoading(true);
    setResult(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('listing-lookup', {
        body: { url, guest_email: capturedEmail?.toLowerCase() || null },
      });

      if (fnError) throw fnError;
      if (!data?.success) {
        if (data?.error?.includes('Too many requests')) {
          throw new Error('rate_limit');
        }
        throw new Error(data?.error || 'Search failed');
      }

      const lensMatches = data.lensMatches || [];
      const foundDirect = lensMatches.length > 0;

      const searchResult: SearchResult = {
        propertyTitle: data.propertyTitle,
        hostName: data.hostName,
        platformPrice: data.platformPrice,
        directPrice: data.directPrice,
        directUrl: foundDirect ? lensMatches[0].link : null,
        platform: data.platform,
        savingsPercent: data.savingsPercent,
        confidence: data.confidence,
        lensMatches,
        foundDirect,
        foundOwnerSite: data.foundOwnerSite || false,
        realPropertyName: data.realPropertyName || null,
        knowledgeGraphUrl: data.knowledgeGraphUrl || null,
        lensRelatedSearches: data.lensRelatedSearches || [],
        mainImageUrl: data.mainImageUrl || null,
      };

      setResult(searchResult);

      // Save property and search request to external Supabase
      const guestEmail = capturedEmail?.toLowerCase() || null;
      try {
        const ownerWebsite = searchResult.foundDirect && searchResult.lensMatches.length > 0
          ? searchResult.lensMatches[0].link
          : null;

        // 1. Upsert property
        const { data: propertyData, error: propertyError } = await externalSupabase
          .from("properties")
          .upsert({
            airbnb_url: url,
            masked_title: data.propertyTitle || null,
            original_name: data.realPropertyName || null,
            city: data.city || null,
            state: data.state || null,
            country: data.country || null,
            bedrooms: data.bedrooms || null,
            description: data.description || null,
            owner_website: ownerWebsite,
            image_url: data.mainImageUrl || null,
            gallery_urls: data.galleryUrls && data.galleryUrls.length > 0 ? data.galleryUrls : [],
            finding_method: data.foundOwnerSite ? "lens+perplexity" : "lens",
          } as any, { onConflict: 'airbnb_url' })
          .select("id")
          .single();

        if (propertyError) {
          console.error("Property save error:", propertyError);
        } else {
          console.log("Property upserted, id:", propertyData?.id);
        }

        // 2. Always insert a search_request with guest_email
        const searchRequestPayload = {
          guest_email: guestEmail,
          listing_url: url,
          property_id: propertyData?.id || null,
          status: searchResult.foundDirect ? "found" : "not_found",
        };
        console.log("Inserting search_request:", searchRequestPayload);

        const { data: searchReqData, error: searchReqError } = await (externalSupabase
          .from("search_requests") as any)
          .insert(searchRequestPayload)
          .select();

        if (searchReqError) {
          console.error("search_requests INSERT error:", searchReqError);
        } else {
          console.log("search_requests INSERT success:", searchReqData);
        }
      } catch (saveErr) {
        console.error("Failed to save property/search data:", saveErr);
      }
    } catch (err: any) {
      if (err?.message === 'rate_limit') {
        setError("You're searching too fast. Please wait a minute and try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-6 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-3">
              Find the direct price
            </h1>
            <p className="text-muted-foreground">
              Paste an Airbnb listing URL below to search for the host's direct booking site.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onSubmit={handleSearchClick}
            className="mb-10"
          >
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.airbnb.com/rooms/..."
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="bg-gradient-hero text-primary-foreground px-6 py-3.5 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity shadow-savings disabled:opacity-60 flex items-center gap-2 whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Searching
                  </>
                ) : (
                  "Search"
                )}
              </button>
            </div>

            <AnimatePresence>
              {urlMissingDetails && url.trim().length > 0 && (
                <div className="mt-3">
                  <BookingDetailsForm value={manualBooking} onChange={setManualBooking} />
                </div>
              )}
            </AnimatePresence>
          </motion.form>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-3 bg-destructive/10 text-destructive border border-destructive/20 rounded-xl px-5 py-3 mb-6 text-sm"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-12"
              >
                <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center mx-auto mb-4 animate-pulse-savings">
                  <Search className="w-5 h-5 text-accent-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">Verifying listing and finding the direct owner website...</p>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {result && !loading && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                <ListingCard result={result} />
                <DirectResultCard result={result} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
      <Footer />

      <EmailCaptureModal
        open={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSuccess={handleEmailSuccess}
        listingUrl={url}
        manualBooking={urlMissingDetails ? manualBooking : undefined}
      />
    </div>
  );
};

export default SearchPage;
